// Each id is the OrganizationProfile field key the card writes to,
// except `socialMediaPage` which is a synthetic group rendered via AboutFormLinks
// (the underlying values land on `instagramUrl`, `tiktokUrl`, `youtubeUrl`,
// `twitchUrl`).
export type AboutYouType = "portfolioUrl" | "introduction";

export type BestWorkType =
  | "portfolioUrl"
  | "mostEngagedPost"
  | "contentExamplePost"
  | "socialMediaPage"
  | "mostImpressivePartnerships";

export type ProfileFieldOption<T extends string> = {
  id: T;
  label: string;
  sub: string;
  placeholder: string;
  multiline: boolean;
  recommended?: boolean;
};

export const ABOUT_YOU_OPTIONS: ProfileFieldOption<AboutYouType>[] = [
  {
    id: "portfolioUrl",
    label: "Portfolio",
    sub: "Full work example",
    placeholder: "https://www.yourportfolio.com",
    multiline: false,
    recommended: true,
  },
  {
    id: "introduction",
    label: "Write about yourself",
    sub: "A short paragraph in your own words",
    placeholder:
      "My name is Minh and I'm a lifestyle creator based in Toronto. I make short-form videos about productivity, slow living, and home decor...",
    multiline: true,
  },
];

export const BEST_WORK_OPTIONS: ProfileFieldOption<BestWorkType>[] = [
  {
    id: "mostEngagedPost",
    label: "Top performing post",
    sub: "Your best performing content",
    placeholder: "https://www.instagram.com/p/your-post-id",
    multiline: false,
  },
  {
    id: "contentExamplePost",
    label: "Brand collab post",
    sub: "Social proof from a past brand",
    placeholder: "https://www.instagram.com/p/your-collab-post-id",
    multiline: false,
  },
  {
    id: "socialMediaPage",
    label: "Social media page",
    sub: "Let brands browse your feed",
    placeholder: "https://instagram.com/yourhandle",
    multiline: false,
  },
  {
    id: "mostImpressivePartnerships",
    label: "Describe your most impressive partnership",
    sub: "Highlight a notable past brand collaboration",
    placeholder:
      "I worked with Olipop on a 4-Reel campaign that drove 1.2M views and a 9% engagement rate — they re-booked me twice...",
    multiline: true,
  },
];

export type WritingStyleSource = "tone" | "gmail" | "paste";

export const WRITING_STYLE_OPTIONS: {
  id: WritingStyleSource;
  label: string;
  sub: string;
  icon: string;
  recommended?: boolean;
}[] = [
  {
    id: "gmail",
    label: "Use my Gmail tone",
    sub: "Learn from emails I've already sent",
    icon: "fa-brands fa-google",
    recommended: true,
  },
  {
    id: "paste",
    label: "Paste sample writings that sounds like you",
    sub: "Add my own writing samples",
    icon: "fa-regular fa-clipboard",
  },
  {
    id: "tone",
    label: "Choose a preset tone",
    sub: "Pick from preset writing styles",
    icon: "fa-solid fa-palette",
  },
];

export const defaultContentForType = (type: BestWorkType): string => {
  return type === "mostEngagedPost" || type === "contentExamplePost"
    ? "https://"
    : "";
};

export const TONE_PRESETS: {
  id: string;
  label: string;
  emoji: string;
}[] = [
  { id: "warm-relatable", label: "Warm & Relatable", emoji: "🏡" },
  { id: "enthusiastic-upbeat", label: "Enthusiastic & Upbeat", emoji: "⚡" },
  { id: "playful-witty", label: "Playful & Witty", emoji: "😄" },
  { id: "professional-formal", label: "Professional & Formal", emoji: "👔" },
  { id: "appreciative-genuine", label: "Appreciative & Genuine", emoji: "🙏" },
  { id: "mission-driven", label: "Mission-Driven", emoji: "🌱" },
  {
    id: "storytelling-evocative",
    label: "Storytelling & Evocative",
    emoji: "🎬",
  },
  { id: "confident-bold", label: "Confident & Bold", emoji: "🔥" },
  { id: "brief-direct", label: "Brief & Direct", emoji: "✂️" },
  {
    id: "strategic-data-driven",
    label: "Strategic & Data-Driven",
    emoji: "📊",
  },
];

export type Method = "ai" | "own";

export type MethodOption = {
  id: Method;
  label: string;
  sub: string;
  icon: string;
  background: string;
  textColor: string;
  subColor: string;
  iconBg: string;
  iconColor: string;
  border?: string;
  checkColor: string;
  recommended?: boolean;
};

export type GeneratingLoadingState = {
  portfolio: boolean;
  emails: boolean;
  generateTemplate: boolean;
  socialMedia: boolean;
};

export const METHOD_OPTIONS: MethodOption[] = [
  {
    id: "ai",
    label: "Bento writes it",
    sub: "AI draft, fully editable",
    icon: "fa-solid fa-wand-magic-sparkles",
    background:
      "linear-gradient(57.59deg, #151F16 -1.58%, #004537 98.35%), linear-gradient(180deg, rgba(236, 234, 229, 0) 0%, #ECEAE5 100%), #FFFFFF",
    textColor: "#FFFFFF",
    subColor: "white",
    iconBg: "#F0F2EE",
    iconColor: "#004537",
    checkColor: "white",
  },
  {
    id: "own",
    label: "Write my own",
    sub: "Start from scratch",
    icon: "fa-solid fa-pen-line",
    background: "#FFFFFF",
    textColor: "#151F16",
    subColor: "primary.main",
    iconBg: "rgba(255, 255, 255, 0.12)",
    iconColor: "primary.main",
    border: "1px solid rgba(21, 31, 22, 0.08)",
    checkColor: "primary.main",
  },
];

export const NICHES: string[] = [
  "Fashion",
  "Lifestyle",
  "Food & Beverage",
  "Travel",
  "Beauty & Personal Care",
  "Sports & Fitness",
  "Technology & Electronics",
];

export type Step2Data = {
  method: Method;
  aboutYouItem: AboutYouType;
  bestWorkItem: BestWorkType;
  writingStyleSource?: WritingStyleSource;
  pitchLength?: "short" | "medium" | "detailed";
  niches?: string[];
};
